import React, { useState, useEffect } from 'react';
import { FiX, FiPlus } from 'react-icons/fi';
import api from '../../services/api';
import Alert from '../Common/Alert';
import { sites as siteMessages, common as commonMessages } from '../../utils/validationMessages';
import ConfirmDialog from '../Common/ConfirmDialog';
import SearchableSelect from '../Common/SearchableSelect';
import AddInvestigatorModal from './AddInvestigatorModal';
import './AddNewSiteModal.css';

const AddNewSiteModal = ({ project, onClose, onSave, initialData }) => {
  const [formData, setFormData] = useState({
    project: project || '',
    investigator: '',
    investigatorEmail: '',
    siteName: '',
    siteEmail: '',
    countryNo: '',
    country: '',
    ecEmail: '',
    recruitmentProjections: '',
    isActive: true,
  });
  const [investigators, setInvestigators] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingInvestigators, setLoadingInvestigators] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showInvestigatorModal, setShowInvestigatorModal] = useState(false);

  useEffect(() => {
    fetchInvestigators();
    fetchCountries();
  }, []);

  // Prefill form when opened from AI Suggested tab with initial data
  useEffect(() => {
    if (!initialData || investigators.length === 0 || countries.length === 0) return;
    const invName = (initialData.keyInvestigator || '').trim();
    const matchInv = investigators.find(
      (inv) => (inv.fullName || inv.name || '').trim().toLowerCase() === invName.toLowerCase()
    );
    const countryName = (initialData.country || '').trim();
    const matchCountry = countries.find(
      (c) => (c.countryNiceName || '').trim().toLowerCase() === countryName.toLowerCase()
    );
    setFormData((prev) => ({
      ...prev,
      project: project || prev.project,
      investigator: matchInv ? (matchInv.fullName || matchInv.name) : invName || '',
      investigatorEmail: matchInv?.email || '',
      siteName: (initialData.siteName || '').trim() || prev.siteName,
      countryNo: matchCountry ? String(matchCountry.countryNo) : '',
      country: matchCountry ? matchCountry.countryNiceName : countryName || prev.country,
    }));
    setFieldErrors({});
  }, [initialData, investigators, countries, project]);

  const fetchInvestigators = async () => {
    try {
      setLoadingInvestigators(true);
      const response = await api.get('/investigators');
      const raw = response.data;
      const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      setInvestigators(list);
    } catch (err) {
      console.error('Error fetching investigators:', err);
      setInvestigators([]);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Could not load investigators.';
      setToast({ type: 'error', message: typeof msg === 'string' ? msg : 'Could not load investigators.' });
    } finally {
      setLoadingInvestigators(false);
    }
  };

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const response = await api.get('/countries');
      setCountries(response.data || []);
    } catch (err) {
      console.error('Error fetching countries:', err);
      setCountries([]);
    } finally {
      setLoadingCountries(false);
    }
  };

  const handleInvestigatorChange = (e) => {
    const selectedInvestigatorName = e.target.value;
    setFieldErrors(prev => ({ ...prev, investigator: '' }));
    if (error) setError(null);

    // Find the selected investigator from the already-fetched list
    const selectedInvestigator = investigators.find(
      inv => (inv.fullName || inv.name) === selectedInvestigatorName
    );

    // Set investigator and email from the fetched list
    setFormData(prev => ({
      ...prev,
      investigator: selectedInvestigatorName,
      investigatorEmail: selectedInvestigator?.email || '', // Use email from the list
    }));
  };

  const handleInvestigatorSaved = (created) => {
    fetchInvestigators();
    const fullName =
      (created?.fullName || created?.name || `${created?.firstName || ''} ${created?.lastName || ''}` || '').trim();
    const email = (created?.email || created?.contactEmail || '').trim();
    if (fullName) {
      setFormData((prev) => ({
        ...prev,
        investigator: fullName,
        investigatorEmail: email || prev.investigatorEmail || '',
      }));
      setFieldErrors((prev) => ({ ...prev, investigator: '' }));
    }
  };

  const handleCountryChange = (e) => {
    const selectedCountryNo = e.target.value;
    const selectedCountry = countries.find(c => c.countryNo.toString() === selectedCountryNo);

    setFormData(prev => ({
      ...prev,
      countryNo: selectedCountryNo,
      country: selectedCountry ? selectedCountry.countryNiceName : '',
    }));
    setFieldErrors(prev => ({ ...prev, country: '' }));
    if (error) setError(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Don't handle investigator change here - use handleInvestigatorChange
    if (name === 'investigator') {
      handleInvestigatorChange(e);
      return;
    }

    // Don't handle country change here - use handleCountryChange
    if (name === 'country') {
      handleCountryChange(e);
      return;
    }

    // No. Of Recruitment Projections: text input, digits only
    if (name === 'recruitmentProjections') {
      const digitsOnly = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: digitsOnly }));
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
      if (error) setError(null);
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (name) setFieldErrors(prev => ({ ...prev, [name]: '' }));
    if (error) setError(null);
  };

  const doCreateSite = async () => {
    const requestData = {
      projectCode: formData.project,
      investigator: formData.investigator,
      investigatorEmail: formData.investigatorEmail || null,
      siteName: formData.siteName,
      siteEmail: formData.siteEmail || null,
      countryNo: formData.countryNo ? parseInt(formData.countryNo) : null,
      ecEmail: formData.ecEmail || null,
      recruitmentProjections: formData.recruitmentProjections ? parseInt(formData.recruitmentProjections) : null,
      isActive: formData.isActive,
      remark: null,
    };
    const response = await api.post('/sites', requestData);
    const createdSite = response.data;
    const siteData = {
      siteId: createdSite.siteId,
      siteNo: createdSite.siteNo,
      piName: createdSite.piName,
      siteName: createdSite.siteName,
      siteAddress: createdSite.siteAddress || formData.country,
      therapeuticArea: createdSite.therapeuticArea || '',
      disease: createdSite.disease || '',
      investigator: formData.investigator,
      investigatorEmail: createdSite.investigatorEmail || formData.investigatorEmail,
      siteEmail: formData.siteEmail,
      country: formData.country,
      ecEmail: formData.ecEmail,
      recruitmentProjections: formData.recruitmentProjections,
      isActive: formData.isActive,
    };
    if (onSave) onSave(siteData);
    setToast({ type: 'success', message: commonMessages.authSuccess });
    onClose();
  };

  const isValidEmail = (value) => {
    if (!value || typeof value !== 'string') return true;
    const trimmed = value.trim();
    if (!trimmed) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.investigator?.trim()) errs.investigator = siteMessages.investigatorRequired;
    const siteNameTrim = formData.siteName?.trim();
    if (!siteNameTrim) errs.siteName = siteMessages.siteNameRequired;
    else if (siteNameTrim.length < 3) errs.siteName = siteMessages.siteNameMinLength;
    if (!formData.countryNo && !formData.country?.trim()) errs.country = siteMessages.countryRequired;
    if (formData.siteEmail?.trim() && !isValidEmail(formData.siteEmail)) errs.siteEmail = siteMessages.emailInvalid;
    if (formData.ecEmail?.trim() && !isValidEmail(formData.ecEmail)) errs.ecEmail = siteMessages.emailInvalid;
    const rec = formData.recruitmentProjections;
    if (rec !== '' && rec != null) {
      const num = parseInt(rec, 10);
      if (Number.isNaN(num)) errs.recruitmentProjections = siteMessages.recruitmentInvalidNumber;
      else if (num < 0) errs.recruitmentProjections = siteMessages.recruitmentNegative;
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleClear = () => {
    setFormData((prev) => ({
      ...prev,
      siteName: '',
      investigator: '',
      investigatorEmail: '',
      siteEmail: '',
      countryNo: '',
      country: '',
      ecEmail: '',
      recruitmentProjections: '',
    }));
    setError(null);
    setFieldErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    if (!validateForm()) return;

    // Show soft validation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmSoftValidation = () => {
    setShowConfirmDialog(false);
    setLoading(true);
    doCreateSite().catch((err) => {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to create site. Please try again.';
      setError(errorMessage);
      console.error('Error creating site:', err);
    }).finally(() => setLoading(false));
  };

  const summaryDisplay = (value) => {
    if (value === null || value === undefined) return '—';
    const s = String(value).trim();
    return s || '';
  };

  const confirmSummaryRows = [
    { label: 'Project', value: formData.project },
    { label: 'Site Name', value: formData.siteName },
    { label: 'Site Email Id', value: formData.siteEmail },
    { label: 'Investigator', value: formData.investigator },
    { label: 'Investigator Email Id', value: formData.investigatorEmail },
    { label: 'Country', value: formData.country },
    { label: 'EC Email', value: formData.ecEmail },
    { label: 'No. Of Recruitment Projections', value: formData.recruitmentProjections },
  ];

  return (
    <>
      <div className="modal-overlay add-new-site-overlay" style={{ zIndex: 2000 }}>
        <div className="modal-content add-new-site-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Add New Site</h3>
            <button className="modal-close" onClick={onClose} disabled={loading}>
              <FiX size={24} />
            </button>
          </div>

          {error && (
            <div className="error-message" style={{
              padding: '12px 24px',
              backgroundColor: '#fee',
              color: '#c00',
              margin: '0 24px',
              borderRadius: '4px',
              border: '1px solid #fcc'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-grid-3">
              <div className="form-group">
                <label className="form-label">Project</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.project}
                  disabled
                />
              </div>

              <div className="form-group">
                <label className="form-label">Site Name <span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  name="siteName"
                  value={formData.siteName}
                  onChange={handleChange}
                />
                {fieldErrors.siteName && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.siteName}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Site Email Id</label>
                <input
                  type="email"
                  className="form-input"
                  name="siteEmail"
                  value={formData.siteEmail}
                  onChange={handleChange}
                />
                {fieldErrors.siteEmail && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.siteEmail}</div>
                )}
              </div>



              
            </div>

            <div className="form-grid-3">
            <div className="form-group">
                <label className="form-label">Investigator <span className="required-asterisk">*</span></label>
                <div className="investigator-select-row">
                  <SearchableSelect
                    name="investigator"
                    value={formData.investigator}
                    onChange={handleChange}
                    options={investigators.map((inv) => ({
                      value: inv.fullName || inv.name,
                      label: inv.fullName || inv.name,
                    }))}
                    placeholder={loadingInvestigators ? 'Loading investigators...' : 'Select Investigator'}
                    disabled={loadingInvestigators}
                    searchPlaceholder="Search by investigator name…"
                    emptyOptionLabel={loadingInvestigators ? 'Loading investigators...' : 'Select Investigator'}
                  />
                  <button
                    type="button"
                    className="audit-icon-button investigator-add-button"
                    title="Add Investigator"
                    onClick={() => setShowInvestigatorModal(true)}
                    disabled={loading}
                  >
                    <FiPlus size={14} />
                  </button>
                </div>
                {fieldErrors.investigator && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.investigator}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Investigator Email Id</label>
                <input
                  type="email"
                  className="form-input"
                  name="investigatorEmail"
                  value={formData.investigatorEmail}
                  onChange={handleChange}
                  disabled={true}
                  placeholder={formData.investigator ? "Auto-filled from database" : "Select investigator to auto-fill"}
                  style={{
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed'
                  }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Country <span className="required-asterisk">*</span></label>
                <SearchableSelect
                  name="country"
                  value={formData.countryNo === '' || formData.countryNo == null ? '' : String(formData.countryNo)}
                  onChange={handleChange}
                  options={countries.map((country) => ({
                    value: String(country.countryNo),
                    label: country.countryNiceName,
                  }))}
                  placeholder={loadingCountries ? 'Loading countries...' : 'Select Country'}
                  disabled={loadingCountries}
                  searchPlaceholder="Search by country name…"
                  emptyOptionLabel={loadingCountries ? 'Loading countries...' : 'Select Country'}
                />
                {fieldErrors.country && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.country}</div>
                )}
              </div>
            </div>

            <div className="form-grid-3">
              <div className="form-group">
                <label className="form-label">EC Email</label>
                <input
                  type="email"
                  className="form-input"
                  name="ecEmail"
                  value={formData.ecEmail}
                  onChange={handleChange}
                />
                {fieldErrors.ecEmail && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.ecEmail}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">No. Of Recruitment Projections</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="form-input"
                  name="recruitmentProjections"
                  value={formData.recruitmentProjections}
                  onChange={handleChange}
                />
                {fieldErrors.recruitmentProjections && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.recruitmentProjections}</div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClear}
                disabled={loading}
              >
                Clear
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Close
              </button>


            </div>
          </form>
        </div>
      </div>
      {showConfirmDialog && (
        <ConfirmDialog
          isOpen={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={handleConfirmSoftValidation}
          title="Confirm Details"
          message="Please review the details below. Confirm email addresses and site contact information before proceeding."
          confirmText="Confirm"
          cancelText="Cancel"
          variant="primary"
          size="medium"
          overlayClassName="confirm-dialog-nested-overlay"
        >
          <table className="confirm-dialog-detail-table">
            <tbody>
              {confirmSummaryRows.map(({ label, value }) => (
                <tr key={label}>
                  <th scope="row">{label}</th>
                  <td>{summaryDisplay(value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ConfirmDialog>
      )}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}
      <AddInvestigatorModal
        isOpen={showInvestigatorModal}
        onClose={() => setShowInvestigatorModal(false)}
        onSaved={handleInvestigatorSaved}
      />
    </>
  );
};

export default AddNewSiteModal;
